import mainStyle from './captivePortal.module.scss';

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { cloneDeep, isEqual, isNil, size } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { useTheme } from '@mui/material/styles';
import { Box, Button, Typography, TextField, IconButton } from '@mui/material';

import { ReactComponent as BinIcon } from 'assets/images/icons/icn_table_cell_Bin.svg';
import { ReactComponent as BinHoverIcon } from 'assets/images/icons/icn_table_cell_Bin_hover.svg';
import { ReactComponent as CaptivePortalIcon } from 'assets/images/icons/icn_dashboard_captive_portal.svg';
import { ReactComponent as AddIcon } from 'assets/images/icons/btn_add.svg';
import { ReactComponent as AddIconDisabled } from 'assets/images/icons/btn_add_disabled.svg';
import { ReactComponent as DeleteIcon } from 'assets/images/icons/icn_table_cell_Bin.svg';

// Utils
import { validDomainName, validUrl, validateIPv4 } from 'utils/validate';

// Hooks
import useDataSourceManager from 'hooks/useDataSourceManager';
import useAuthScope from 'hooks/useAuthScope';

// Const
import { authTypeConfig, applyAuthTypeFieldDefaults } from 'pages/System/RealmSetting/components/CaptivePortal/const';

// Components
import LinkModal from 'components/LinkModal';
import TabsField from 'components/Tab';
import CollapseCard from 'components/Collapse';
import GlobalAlert from 'components/Notification';
import { MenuSelect } from 'components/Select/menuSelect';
import { RadioButton } from 'components/buttons/RadioButton';
import { SolidButton } from 'components/buttons/SolidButton';
import { OutlineButton } from 'components/buttons/OutlineButton';
import { OutlineRectangleButton } from 'components/buttons/OutlineRectangleButton';

import Loading from 'pages/System/RealmSetting/components/CaptivePortal/components/Loading';
import UrlInput from 'pages/System/RealmSetting/components/CaptivePortal/components/UrlInput';
import SelectMenu from 'pages/System/RealmSetting/components/CaptivePortal/components/SelectMenu';
import { TypeSelectMenu } from 'pages/System/RealmSetting/components/CaptivePortal/components/TypeSelectMenu';

const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;

const MAX_WALLED_GARDEN_ENTRIES = 20;

const isValidWalledGardenValue = value => {
  const normalizedValue = value?.trim();
  if (!normalizedValue) return false;

  if (validateIPv4(normalizedValue) || validDomainName(normalizedValue)) {
    return true;
  }

  const [ipAddress, prefixLength] = normalizedValue.split('/');
  if (!ipAddress || prefixLength === undefined || !validateIPv4(ipAddress) || !/^\d+$/.test(prefixLength)) {
    return false;
  }

  const prefix = Number(prefixLength);
  return prefix >= 0 && prefix <= 32;
};

const EditCaptivePortalContent = ({
  drawerOpen,
  setDrawerOpen,
  setSelectedRowItem,
  setOpenRemoveProfileModal,
  tableData,
  originalRowData,
  reloadPage,
  initCaptivePortal,
  formData,
  setFormData,
  showEditGeneral,
  isUnityPro,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { auth } = useAuthScope();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const [updateCaptivePortalUuid, _setUpdateCaptivePortalUuid] = useState(uuidv4());

  const [defaultFormData, setDefaultFormData] = useState({});
  const [tabValue, setTabValue] = useState('settings');
  const [hasChanged, setHasChanged] = useState(false);
  const [hasDuplicateName, setHasDuplicateName] = useState(false);
  const [walledGardenInputs, setWalledGardenInputs] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });

  const textfieldStyle = {
    width: '280px',
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.customBorderRadius[1],
      height: '30px',
      '& fieldset': {
        borderColor: theme.palette.primary.borderGreyPrimary,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.mainHover,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    // Hide native number spinners (WebKit)
    '& .MuiOutlinedInput-root input[type="number"]::-webkit-outer-spin-button, & .MuiOutlinedInput-root input[type="number"]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    // Firefox / generic appearance
    '& .MuiOutlinedInput-root input[type="number"]': {
      MozAppearance: 'textfield',
      appearance: 'textfield',
    },
  };

  const labelStyle = {
    width: '160px',
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[0],
  };

  const rowStyle = {
    display: 'flex',
    gap: '18px',
    alignItems: 'center',
    width: '100%',
  };

  const tabData = [
    {
      name: 'Settings',
      value: 'settings',
      disabled: null,
      icon: null,
    },
    {
      name: 'Advanced',
      value: 'advanced',
      disabled: null,
      icon: null,
    },
  ];

  const parseCaptivePortalInfo = isOriginalRowData => {
    const newFormData = cloneDeep(formData);
    const rowData = cloneDeep(isOriginalRowData);

    newFormData['id'] = rowData?.config?.id || '';

    newFormData['profileName'] = rowData?.config?.name || '';

    newFormData['authTypeList'] = newFormData.authTypeList.map(item => ({ ...item, isActive: item.value === rowData?.config?.authType }));

    newFormData['simultaneousLoginOption'] = rowData?.config?.simultaneousLogin ? 'ENABLE' : 'DISABLE';

    newFormData['idleTimeoutOption'] = rowData?.config?.idleTimeoutOption;
    if (rowData?.config?.idleTimeoutOption === 'DEFAULT') {
      newFormData['idleTimeoutList'] = newFormData.idleTimeoutList.map(item => ({ ...item, isActive: item.value === rowData?.config?.idleTimeout }));
    } else {
      newFormData['idleTimeoutLabel'] = rowData?.config?.idleTimeout; // CUSTOM
    }

    newFormData['authTimeoutOption'] = rowData?.config?.authTimeoutOption;
    if (rowData?.config?.authTimeoutOption === 'ENABLE') {
      newFormData['authTimeoutList'] = newFormData.authTimeoutList.map(item => ({ ...item, isActive: item.value === rowData?.config?.authTimeout }));
    } else if (rowData?.config?.authTimeoutOption === 'CUSTOM') {
      newFormData['authTimeoutLabel'] = rowData?.config?.authTimeout; // CUSTOM
    } else {
      newFormData['authTimeoutLabel'] = 5; // DISABLE
    }

    newFormData['authPerDayOption'] = rowData?.config?.authPerDayOption;
    if (rowData?.config?.authPerDayOption === 'CUSTOM') {
      newFormData['authPerDayLabel'] = rowData?.config?.authPerDay;
    } else {
      newFormData['authPerDayLabel'] = 5; // UNLIMIT
    }

    newFormData['reAuthIntervalOption'] = rowData?.config?.reAuthIntervalOption;
    if (rowData?.config?.reAuthIntervalOption === 'ENABLE') {
      newFormData['reAuthIntervalList'] = newFormData.reAuthIntervalList.map(item => ({ ...item, isActive: item.value === rowData?.config?.reAuthInterval }));
    } else if (rowData?.config?.reAuthIntervalOption === 'CUSTOM') {
      newFormData['reAuthIntervalLabel'] = rowData?.config?.reAuthInterval; // CUSTOM
    } else {
      newFormData['reAuthIntervalLabel'] = 10; // DISABLE
    }

    newFormData['landingPageOption'] = rowData?.config?.landingPageOption;
    if (rowData?.config?.landingPageOption === 'CUSTOM') {
      newFormData['landingPageUrlProtocol'] = rowData?.config?.landingPageUrl.split('://')?.[0];
      newFormData['landingPageUrlLabel'] = rowData?.config?.landingPageUrl.split('://')?.[1];
    }

    const userProfileId = rowData?.config?.userProfile || '';
    newFormData['userProfileList'] = newFormData.userProfileList.map(item => ({ ...item, isActive: item?.value === userProfileId }));

    const radiusProfileId = rowData?.config?.radiusProfile || '';
    newFormData['radiusProfileList'] = newFormData.radiusProfileList.map(item => ({ ...item, isActive: item?.value === radiusProfileId }));

    const ldapProfileId = rowData?.config?.ldapProfile || '';
    newFormData['ldapProfileList'] = newFormData.ldapProfileList.map(item => ({ ...item, isActive: item?.value === ldapProfileId }));

    if (rowData?.config?.externalCaptivePortal) {
      newFormData['externalCaptivePortalProtocol'] = rowData?.config?.externalCaptivePortal.split('://')?.[0];
      newFormData['externalCaptivePortalLabel'] = rowData?.config?.externalCaptivePortal.split('://')?.[1];
    } else {
      newFormData['externalCaptivePortalProtocol'] = 'http';
      newFormData['externalCaptivePortalLabel'] = '';
    }

    const whitelistId = rowData?.config?.macWhiteList || '';
    newFormData['whitelistList'] = newFormData.whitelistList.map(item => ({ ...item, isActive: item?.value === whitelistId }));

    newFormData['preAuthAccessOption'] = rowData?.config?.walledGarden ? 'ENABLE' : 'DISABLE';
    newFormData['walledGardenList'] = rowData?.config?.walledGardenList?.map(item => ({ label: item, value: item })) || [{ label: '', value: '' }];
    setWalledGardenInputs(newFormData?.walledGardenList?.map(item => item?.label || '') || []);

    const currentAuthType = rowData?.config?.authType || newFormData?.authTypeList?.find(item => item.isActive)?.value || 'CLICK_THROUGH';
    applyAuthTypeFieldDefaults(newFormData, currentAuthType);

    setFormData(newFormData);
    setDefaultFormData(newFormData);
  };

  const getIdleTimeoutOption = () => {
    return formData?.idleTimeoutOption || 'DEFAULT';
  };

  const getIdleTimeoutValue = () => {
    if (formData?.idleTimeoutOption === 'DEFAULT') {
      return formData?.idleTimeoutList?.find(item => item.isActive)?.value;
    } else {
      return formData?.idleTimeoutLabel; // CUSTOM
    }
  };

  const getAuthTimeoutOption = () => {
    return formData?.authTimeoutOption || 'DISABLE';
  };

  const getAuthTimeoutValue = () => {
    if (formData?.authTimeoutOption === 'ENABLE') {
      return formData?.authTimeoutList?.find(item => item.isActive)?.value;
    } else if (formData?.authTimeoutOption === 'CUSTOM') {
      return formData?.authTimeoutLabel;
    } else {
      return 2; // DISABLE
    }
  };

  const getAuthPerDayOption = () => {
    return formData?.authPerDayOption || 'CUSTOM';
  };

  const getAuthPerDayValue = () => {
    if (formData?.authPerDayOption === 'CUSTOM') {
      return formData?.authPerDayLabel;
    } else {
      return 1; // UNLIMIT
    }
  };

  const getReAuthIntervalOption = () => {
    return formData?.reAuthIntervalOption || 'DISABLE';
  };

  const getReAuthIntervalValue = () => {
    if (formData?.reAuthIntervalOption === 'ENABLE') {
      return formData?.reAuthIntervalList?.find(item => item.isActive)?.value;
    } else if (formData?.reAuthIntervalOption === 'CUSTOM') {
      return formData?.reAuthIntervalLabel;
    } else {
      return 1; // DISABLE
    }
  };

  const getUserProfileValue = () => {
    return formData?.userProfileList?.find(item => item.isActive)?.value || '';
  };

  const getRadiusProfileValue = () => {
    return formData?.radiusProfileList?.find(item => item.isActive)?.value || '';
  };

  const getLdapProfileValue = () => {
    return formData?.ldapProfileList?.find(item => item.isActive)?.value || '';
  };

  const getExternalCaptivePortalValue = () => {
    if (formData?.externalCaptivePortalProtocol && formData?.externalCaptivePortalLabel) {
      return `${formData?.externalCaptivePortalProtocol}://${formData?.externalCaptivePortalLabel}`;
    } else {
      return '';
    }
  };

  const getLandingPageOption = () => {
    return formData?.landingPageOption || 'NO_SPECIFIC';
  };

  const getLandingPageValue = () => {
    if (formData?.landingPageOption === 'CUSTOM') {
      return `${formData?.landingPageUrlProtocol}://${formData?.landingPageUrlLabel}`;
    } else {
      return '';
    }
  };

  const getWalledGardenListValue = () => {
    return formData?.walledGardenList?.map(item => item?.label?.trim()).filter(Boolean) || [];
  };

  const checkProfileNameInvalid = () => {
    return hasDuplicateName || !formData?.profileName;
  };

  const checkIdleTimeoutInvalid = () => {
    if (formData?.idleTimeoutOption === 'CUSTOM') {
      return formData?.idleTimeoutLabel < 2 || formData?.idleTimeoutLabel > 1440;
    }
    return false;
  };

  const checkAuthTimeoutInvalid = () => {
    if (formData?.authTimeoutOption === 'CUSTOM') {
      return formData?.authTimeoutLabel < 2 || formData?.authTimeoutLabel > 1440;
    } else if (formData?.authTimeoutOption === 'DISABLE') {
      return false;
    }
    return false;
  };

  const checkAuthPerDayInvalid = () => {
    if (formData?.authPerDayOption === 'CUSTOM') {
      return formData?.authPerDayLabel < 1 || formData?.authPerDayLabel > 720;
    }
    return false;
  };

  const checkReAuthIntervalInvalid = () => {
    if (formData?.reAuthIntervalOption === 'CUSTOM') {
      return formData?.reAuthIntervalLabel < 1 || formData?.reAuthIntervalLabel > 1440;
    }
    return false;
  };

  const checkUserProfileInvalid = () => {
    return !formData?.userProfileList?.find(item => item.isActive)?.value;
  };

  const checkRadiusProfileInvalid = () => {
    return !formData?.radiusProfileList?.find(item => item.isActive)?.value;
  };

  const checkLdapProfileInvalid = () => {
    return !formData?.ldapProfileList?.find(item => item.isActive)?.value;
  };

  const checkExternalCaptivePortalInvalid = () => {
    if (formData?.externalCaptivePortalProtocol || formData?.externalCaptivePortalLabel) {
      return !formData?.externalCaptivePortalLabel || !validUrl(formData?.externalCaptivePortalLabel);
    }
    return false;
  };

  const checkLandingPageInvalid = () => {
    if (formData?.landingPageOption === 'CUSTOM') {
      return !formData?.landingPageUrlLabel || !validUrl(formData?.landingPageUrlLabel);
    }
    return false;
  };

  const checkWalledGardenEntryInvalid = () => {
    if (formData?.preAuthAccessOption === 'ENABLE') {
      const hasInvalidWalledGardenEntry =
        formData?.walledGardenList?.some(item => {
          const currentValue = item?.label || '';
          return currentValue && !isValidWalledGardenValue(currentValue);
        }) || false;
      return hasInvalidWalledGardenEntry;
    }
    return false;
  };

  const checkWalledGardenDuplicate = () => {
    const walledGardenValues = formData?.walledGardenList?.map(item => item?.label?.trim()).filter(Boolean) || [];
    const uniqueValues = new Set(walledGardenValues);
    return uniqueValues.size !== walledGardenValues.length;
  };

  // Check whether the value at a specific walled garden input index is duplicated
  const isWalledGardenValueDuplicate = index => {
    const values = (walledGardenInputs || []).map(v => v?.trim()).filter(Boolean);
    const val = (walledGardenInputs?.[index] || '').trim();
    if (!val) return false;
    const count = values.filter(v => v === val).length;
    return count > 1;
  };

  const checkWalledGardenInvalid = () => {
    if (formData?.preAuthAccessOption === 'ENABLE') {
      const walledGardenEntry = checkWalledGardenEntryInvalid();
      const walledGardenDuplicate = checkWalledGardenDuplicate();
      const walledGardenCountInvalid = (formData?.walledGardenList?.length || 0) > MAX_WALLED_GARDEN_ENTRIES;
      const walledGardenEmptyInvalid = formData?.walledGardenList?.some(item => !item?.label?.trim());
      const walledGardenAllEmptyInvalid = formData?.walledGardenList?.every(item => !item?.label?.trim());
      return walledGardenEntry || walledGardenDuplicate || walledGardenCountInvalid || walledGardenEmptyInvalid || walledGardenAllEmptyInvalid;
    }
    return false;
  };

  const checkConfigInvalid = () => {
    let isInvalid = false;

    switch (formData?.authTypeList?.find(item => item.isActive)?.value) {
      case 'USERNAME_PASSWORD':
        isInvalid =
          checkIdleTimeoutInvalid() ||
          checkAuthTimeoutInvalid() ||
          checkAuthPerDayInvalid() ||
          checkReAuthIntervalInvalid() ||
          checkUserProfileInvalid() ||
          checkLandingPageInvalid() ||
          checkWalledGardenInvalid();
        break;
      case 'VOUCHER':
        isInvalid = checkLandingPageInvalid() || checkWalledGardenInvalid();
        break;
      case 'REMOTE_RADIUS':
        isInvalid =
          checkIdleTimeoutInvalid() ||
          checkAuthTimeoutInvalid() ||
          checkAuthPerDayInvalid() ||
          checkReAuthIntervalInvalid() ||
          checkRadiusProfileInvalid() ||
          checkLandingPageInvalid() ||
          checkWalledGardenInvalid();
        break;
      case 'LDAP':
        isInvalid =
          checkIdleTimeoutInvalid() ||
          checkAuthTimeoutInvalid() ||
          checkAuthPerDayInvalid() ||
          checkReAuthIntervalInvalid() ||
          checkLdapProfileInvalid() ||
          checkLandingPageInvalid() ||
          checkWalledGardenInvalid();
        break;
      case 'MAC_ADDRESS':
        isInvalid = checkIdleTimeoutInvalid() || checkRadiusProfileInvalid() || checkWalledGardenInvalid();
        break;
      case 'EXTERNAL_CAPTIVE_PORTAL':
        isInvalid = checkIdleTimeoutInvalid() || checkExternalCaptivePortalInvalid() || checkLandingPageInvalid() || checkWalledGardenInvalid();
        break;
      default:
        // CLICK_THROUGH
        isInvalid = checkAuthTimeoutInvalid() || checkAuthPerDayInvalid() || checkReAuthIntervalInvalid() || checkLandingPageInvalid() || checkWalledGardenInvalid();
        break;
    }

    return checkProfileNameInvalid() || macLimitExceeded || isInvalid;
  };

  const handleChangeProfileName = newValue => {
    setFormData(prev => ({ ...prev, profileName: newValue }));

    const tmpTableData = cloneDeep(tableData)?.filter(item => item?.captivePortalName !== originalRowData?.captivePortalName);
    const hasDuplicate = tmpTableData.some(item => item.captivePortalName === newValue);
    setHasDuplicateName(hasDuplicate);
  };

  const handleChangeWalledGardenItem = (index, newValue) => {
    setFormData(prev => ({
      ...prev,
      walledGardenList: prev.walledGardenList.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              label: newValue,
              value: newValue?.trim(),
            }
          : item,
      ),
    }));
  };

  const handleAddWalledGardenItem = () => {
    if ((formData?.walledGardenList?.length || 0) >= MAX_WALLED_GARDEN_ENTRIES) return;

    setFormData(prev => ({
      ...prev,
      walledGardenList: [...(prev.walledGardenList || []), { label: '', value: '' }],
    }));

    // keep the visible input values in sync when adding
    setWalledGardenInputs(prev => [...(prev || []), '']);
  };

  const handleRemoveWalledGardenItem = index => {
    setFormData(prev => {
      const nextList = prev.walledGardenList.filter((_, itemIndex) => itemIndex !== index);
      return {
        ...prev,
        walledGardenList: nextList.length > 0 ? nextList : [{ label: '', value: '' }],
      };
    });

    // also remove the corresponding input value so new inputs don't reuse deleted values
    setWalledGardenInputs(prev => {
      const next = (prev || []).filter((_, i) => i !== index);
      return next.length > 0 ? next : [''];
    });
  };

  const handleChangeItem = (list, newValue) => {
    const newFormData = cloneDeep(formData);

    if (list === 'authTypeList') {
      const currentType = formData?.authTypeList?.find(item => item.isActive)?.value;
      if (currentType === newValue) return;

      if (newFormData?.[list]?.length > 0) {
        newFormData[list] = newFormData[list].map(item => ({
          ...item,
          isActive: item.value === newValue,
        }));
      }

      applyAuthTypeFieldDefaults(newFormData, newValue);
      setFormData(newFormData);
      return;
    }

    if (newFormData?.[list]?.length > 0) {
      newFormData[list] = newFormData?.[list]?.map(item => ({
        ...item,
        isActive: item.value === newValue,
      }));
    }
    setFormData(newFormData);
  };

  const handleChangeResetBtn = () => {
    setHasDuplicateName(false);
    setWalledGardenInputs(defaultFormData?.walledGardenList?.map(item => item?.label || '') || []);
    setFormData(defaultFormData); // Reset
  };

  const handleClickApplyBtn = () => {
    setIsLoading(true);

    const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/settings/captivePortals/:captivePortalId.update`,
      site: `${baseUrl}/sites/:siteId/settings/captivePortals/:captivePortalId.update`,
    };

    const apiKey = apiConfig[auth.scopeLevel];

    const restfulParams = standalone
      ? { orgId: auth.realm.id, siteId: auth.sites?.id, captivePortalId: formData?.id || '' }
      : {
          entId: auth.realm.id,
          orgId: auth.orgs.id,
          siteId: auth.sites?.id,
          captivePortalId: formData?.id || '',
        };

    const isSelectedAuthType = formData?.authTypeList?.find(item => item.isActive)?.value || 'CLICK_THROUGH';

    const payload = {
      id: formData?.id || '',
      name: formData?.profileName || '',
      authType: formData?.authTypeList?.find(item => item.isActive)?.value || 'CLICK_THROUGH',
      simultaneousLogin: formData?.simultaneousLoginOption === 'ENABLE' ? true : false,
      macWhiteList: formData?.whitelistList?.find(item => item.isActive)?.value || '',
      walledGarden: formData?.preAuthAccessOption === 'ENABLE' ? true : false,
      walledGardenList: formData?.preAuthAccessOption === 'ENABLE' ? getWalledGardenListValue() : [],
    };

    switch (isSelectedAuthType) {
      case 'USERNAME_PASSWORD':
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['authTimeoutOption'] = getAuthTimeoutOption();
        payload['authTimeout'] = getAuthTimeoutValue();
        payload['authPerDayOption'] = getAuthPerDayOption();
        payload['authPerDay'] = getAuthPerDayValue();
        payload['reAuthIntervalOption'] = getReAuthIntervalOption();
        payload['reAuthInterval'] = getReAuthIntervalValue();
        payload['userProfile'] = getUserProfileValue();
        payload['landingPageOption'] = getLandingPageOption();
        payload['landingPageUrl'] = getLandingPageValue();
        break;
      case 'VOUCHER':
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['landingPageOption'] = getLandingPageOption();
        payload['landingPageUrl'] = getLandingPageValue();
        break;
      case 'REMOTE_RADIUS':
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['authTimeoutOption'] = getAuthTimeoutOption();
        payload['authTimeout'] = getAuthTimeoutValue();
        payload['authPerDayOption'] = getAuthPerDayOption();
        payload['authPerDay'] = getAuthPerDayValue();
        payload['reAuthIntervalOption'] = getReAuthIntervalOption();
        payload['reAuthInterval'] = getReAuthIntervalValue();
        payload['radiusProfile'] = getRadiusProfileValue();
        payload['landingPageOption'] = getLandingPageOption();
        payload['landingPageUrl'] = getLandingPageValue();
        break;
      case 'LDAP':
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['authTimeoutOption'] = getAuthTimeoutOption();
        payload['authTimeout'] = getAuthTimeoutValue();
        payload['authPerDayOption'] = getAuthPerDayOption();
        payload['authPerDay'] = getAuthPerDayValue();
        payload['reAuthIntervalOption'] = getReAuthIntervalOption();
        payload['reAuthInterval'] = getReAuthIntervalValue();
        payload['ldapProfile'] = getLdapProfileValue();
        payload['landingPageOption'] = getLandingPageOption();
        payload['landingPageUrl'] = getLandingPageValue();
        break;
      case 'MAC_ADDRESS':
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['radiusProfile'] = getRadiusProfileValue();
        break;
      case 'EXTERNAL_CAPTIVE_PORTAL':
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['externalCaptivePortal'] = getExternalCaptivePortalValue();
        payload['landingPageOption'] = getLandingPageOption();
        payload['landingPageUrl'] = getLandingPageValue();
        break;
      default:
        // CLICK_THROUGH
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['authTimeoutOption'] = getAuthTimeoutOption();
        payload['authTimeout'] = getAuthTimeoutValue();
        payload['authPerDayOption'] = getAuthPerDayOption();
        payload['authPerDay'] = getAuthPerDayValue();
        payload['reAuthIntervalOption'] = getReAuthIntervalOption();
        payload['reAuthInterval'] = getReAuthIntervalValue();
        payload['landingPageOption'] = getLandingPageOption();
        payload['landingPageUrl'] = getLandingPageValue();
        break;
    }

    sendRequest(apiKey, updateCaptivePortalUuid, restfulParams, payload);
  };

  const checkSaveProRule = () => {
    const basicTypeArray = ['CLICK_THROUGH', 'USERNAME_PASSWORD'];
    if (!isUnityPro && !basicTypeArray.includes(formData?.authTypeList?.find(item => item.isActive)?.value)) {
      setIsLinkModalOpen(true);
      return; // Open the link modal first.
    }

    handleClickApplyBtn();
  };

  // Check which authType is selected and get the corresponding settings from authTypeConfig
  const selectedAuthType = useMemo(() => {
    return formData?.authTypeList?.find(item => item.isActive)?.value ?? 'CLICK_THROUGH';
  }, [formData?.authTypeList]);

  const authTypeSetting = authTypeConfig?.[selectedAuthType] ?? {};

  const showSimultaneousLoginInfo = authTypeSetting.simultaneousLogin ?? false;
  const showIdleTimeoutInfo = authTypeSetting.idleTimeout ?? false;
  const showAuthTimeoutInfo = authTypeSetting.authTimeout ?? false;
  const showAuthPerDayInfo = authTypeSetting.authPerDay ?? false;
  const showReAuthIntervalInfo = authTypeSetting.reAuthInterval ?? false;
  const showUserProfileInfo = authTypeSetting.userProfile ?? false;
  const showRadiusProfileInfo = authTypeSetting.radiusProfile ?? false;
  const showLdapProfileInfo = authTypeSetting.ldapProfile ?? false;
  const showExternalCaptivePortalInfo = authTypeSetting.externalCaptivePortal ?? false;
  const showLandingPageInfo = authTypeSetting.landingPage ?? false;
  const showWhitelistInfo = authTypeSetting.whitelist ?? false;
  const showPreAuthAccessInfo = authTypeSetting.preAuthAccess ?? false;

  const macLimitExceeded = formData?.whitelistList?.find(item => item.isActive === true)?.macListLength > 64;

  useEffect(() => {
    if (drawerOpen && originalRowData) {
      setTabValue('settings');
      setHasDuplicateName(false);
      parseCaptivePortalInfo(originalRowData);
    }
  }, [drawerOpen, originalRowData]);

  useEffect(() => {
    if (!Object.keys(formData).length || !Object.keys(defaultFormData).length) {
      return;
    }

    if (!isEqual(formData, defaultFormData) || checkConfigInvalid()) {
      setHasChanged(true);
    } else {
      setHasChanged(false);
    }
  }, [formData, defaultFormData]);

  useEffect(() => {
    const isUpdateResponse = getData(updateCaptivePortalUuid);
    setIsLoading(false);
    if (!isUpdateResponse) return;

    if (checkMockup) {
      if (isUpdateResponse && isUpdateResponse?.data) {
        reloadPage(isUpdateResponse.data);
        setDrawerOpen(false);
        setSelectedRowItem(null);
      }
    } else {
      if (isUpdateResponse) {
        if (isUpdateResponse.hasOwnProperty('code') && isUpdateResponse?.code !== 200) {
          setAlertInfo({
            open: true,
            severity: 'error',
            message: isUpdateResponse?.message,
          });
        } else {
          initCaptivePortal();
          setDrawerOpen(false);
          setSelectedRowItem(null);
        }
      }
    }
  }, [dataMap]);

  return (
    <Box sx={{ maxWidth: '600px', height: '110%', position: 'relative' }}>
      {isLoading && <Loading />}

      <Box sx={{ display: 'flex', paddingBottom: 2, px: 4 }}>
        <CaptivePortalIcon />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            marginLeft: 1,
            overflowWrap: 'anywhere',
          }}
        >
          <Box sx={{ fontWeight: 600, fontSize: '20px', color: '#373737' }}>{originalRowData?.captivePortalName}</Box>
          <Box>{originalRowData?.authTypeLabel}</Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <TabsField
          tabsStyles={{
            '& .MuiButtonBase-root': {
              margin: '0 20px',
            },
          }}
          tabVal={tabValue}
          handleTabsChange={(e, newValue) => setTabValue(newValue)}
          tabData={tabData}
        />
      </Box>

      {/* Tab - Settings */}
      {tabValue === 'settings' && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            px: 4,
            boxSizing: 'border-box',
            gap: '20px',
          }}
        >
          {/* General */}
          <CollapseCard title="General">
            <Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: 1 }}>
              {/* Profile Name */}
              <Box sx={rowStyle}>
                <Box sx={{ ...labelStyle, paddingTop: hasDuplicateName ? '0px' : '10px', height: hasDuplicateName ? '52px' : '50px' }}>
                  <Typography className={mainStyle['collapse-typography']}>Profile Name</Typography>
                </Box>
                <Box sx={labelStyle}>
                  <TextField
                    placeholder="e.g. Pro WiFi Access"
                    sx={textfieldStyle}
                    autoComplete="off"
                    inputProps={{
                      minLength: 1,
                      maxLength: 64,
                    }}
                    value={formData?.profileName || ''}
                    onChange={e => handleChangeProfileName(e.target.value)}
                    error={hasDuplicateName || !formData?.profileName}
                    helperText={hasDuplicateName ? 'This name already exists in this site.' : ''}
                    disabled={!showEditGeneral}
                  />
                </Box>
              </Box>

              {/* Authentication Type */}
              <Box sx={{ ...rowStyle, marginTop: 2 }}>
                <Box sx={labelStyle}>
                  <Typography className={mainStyle['collapse-typography']}>Authentication Type</Typography>
                </Box>
                <Box sx={labelStyle}>
                  <TypeSelectMenu
                    width={textfieldStyle.width}
                    value={formData?.authTypeList?.find(item => item.isActive)?.value}
                    options={formData?.authTypeList}
                    onChange={obj => handleChangeItem('authTypeList', obj.value)}
                    disabled={!showEditGeneral}
                    isUnityPro={isUnityPro}
                  />
                </Box>
              </Box>

              {/* Simultaneous Login */}
              {showSimultaneousLoginInfo && (
                <Box sx={{ ...rowStyle, marginTop: 1 }}>
                  <Box sx={labelStyle}>
                    <Typography className={mainStyle['collapse-typography']}>Simultaneous Login</Typography>
                  </Box>
                  <Box>
                    <div style={{ display: 'flex' }}>
                      <div style={{ width: 100 }}>
                        <RadioButton
                          label="Enabled"
                          name="simultaneousLoginOption"
                          value={'ENABLE'}
                          checked={formData?.simultaneousLoginOption === 'ENABLE'}
                          onChange={() => setFormData(prev => ({ ...prev, simultaneousLoginOption: 'ENABLE' }))}
                          disabled={!showEditGeneral}
                        />
                      </div>
                      <div style={{ width: 100 }}>
                        <RadioButton
                          label="Disabled"
                          name="simultaneousLoginOption"
                          value={'DISABLE'}
                          checked={formData?.simultaneousLoginOption === 'DISABLE'}
                          onChange={() => setFormData(prev => ({ ...prev, simultaneousLoginOption: 'DISABLE' }))}
                          disabled={!showEditGeneral}
                        />
                      </div>
                    </div>
                  </Box>
                </Box>
              )}

              {/* Idle Timeout */}
              {showIdleTimeoutInfo && (
                <Box sx={{ ...rowStyle, alignItems: 'flex-start', marginTop: 1 }}>
                  <Box
                    sx={{
                      ...labelStyle,
                      marginTop: '4px',
                      height: formData?.idleTimeoutOption === 'DEFAULT' ? '20px' : '50px',
                    }}
                  >
                    <Typography className={mainStyle['collapse-typography']}>Idle Timeout</Typography>
                  </Box>
                  <Box sx={labelStyle}>
                    <Box style={{ display: 'flex' }}>
                      <Box sx={{ minWidth: '100px' }}>
                        <RadioButton
                          label="Default"
                          name="idleTimeoutOption"
                          value={'DEFAULT'}
                          checked={formData?.idleTimeoutOption === 'DEFAULT'}
                          onChange={() => setFormData(prev => ({ ...prev, idleTimeoutOption: 'DEFAULT' }))}
                          disabled={!showEditGeneral}
                        />
                      </Box>
                      <Box sx={{ minWidth: '100px' }}>
                        <RadioButton
                          label="Custom"
                          name="idleTimeoutOption"
                          value={'CUSTOM'}
                          checked={formData?.idleTimeoutOption === 'CUSTOM'}
                          onChange={() => setFormData(prev => ({ ...prev, idleTimeoutOption: 'CUSTOM' }))}
                          disabled={!showEditGeneral}
                        />
                      </Box>
                    </Box>
                    {formData?.idleTimeoutOption === 'DEFAULT' && (
                      <MenuSelect
                        width={textfieldStyle.width}
                        value={formData?.idleTimeoutList?.find(item => item.isActive)?.value}
                        options={formData?.idleTimeoutList}
                        onChange={obj => handleChangeItem('idleTimeoutList', obj.value)}
                        disabled={!showEditGeneral}
                      />
                    )}
                    {formData?.idleTimeoutOption === 'CUSTOM' && (
                      <Box sx={{ position: 'relative' }}>
                        <Box
                          sx={{
                            position: 'absolute',
                            right: '-110px',
                            top: '5px',
                            color: '#aaa',
                            pointerEvents: 'none',
                          }}
                        >
                          Min(s)
                        </Box>

                        <TextField
                          sx={textfieldStyle}
                          autoComplete="off"
                          type="number"
                          min="2"
                          max="1440"
                          value={formData?.idleTimeoutLabel}
                          onChange={e => setFormData(prev => ({ ...prev, idleTimeoutLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                          error={formData?.idleTimeoutLabel < 2 || formData?.idleTimeoutLabel > 1440}
                          disabled={!showEditGeneral}
                        />
                        <Typography sx={{ marginTop: 1, color: theme.palette.primary.greySecound }}>(2~1440)</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Authentication Timeout */}
              {showAuthTimeoutInfo && (
                <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      ...labelStyle,
                      marginTop: '4px',
                      height: formData?.authTimeoutOption === 'DISABLE' ? '20px' : '50px',
                    }}
                  >
                    <Typography className={mainStyle['collapse-typography']}>Authentication Timeout</Typography>
                  </Box>
                  <Box sx={labelStyle}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Box sx={{ minWidth: '100px' }}>
                        <RadioButton
                          label="Disabled"
                          name="authTimeoutOption"
                          value={'DISABLE'}
                          checked={formData?.authTimeoutOption === 'DISABLE'}
                          onChange={() => setFormData(prev => ({ ...prev, authTimeoutOption: 'DISABLE' }))}
                          disabled={!showEditGeneral}
                        />
                      </Box>
                      <Box sx={{ minWidth: '100px' }}>
                        <RadioButton
                          label="Enabled"
                          name="authTimeoutOption"
                          value={'ENABLE'}
                          checked={formData?.authTimeoutOption === 'ENABLE'}
                          onChange={() => setFormData(prev => ({ ...prev, authTimeoutOption: 'ENABLE' }))}
                          disabled={!showEditGeneral}
                        />
                      </Box>
                      <Box sx={{ minWidth: '100px' }}>
                        <RadioButton
                          label="Custom"
                          name="authTimeoutOption"
                          value={'CUSTOM'}
                          checked={formData?.authTimeoutOption === 'CUSTOM'}
                          onChange={() => setFormData(prev => ({ ...prev, authTimeoutOption: 'CUSTOM' }))}
                          disabled={!showEditGeneral}
                        />
                      </Box>
                    </Box>

                    {formData?.authTimeoutOption === 'ENABLE' && (
                      <MenuSelect
                        width={textfieldStyle.width}
                        value={formData?.authTimeoutList?.find(item => item.isActive)?.value}
                        options={formData?.authTimeoutList}
                        onChange={obj => handleChangeItem('authTimeoutList', obj.value)}
                        disabled={!showEditGeneral}
                      />
                    )}
                    {formData?.authTimeoutOption === 'CUSTOM' && (
                      <Box sx={{ position: 'relative' }}>
                        <Box
                          sx={{
                            position: 'absolute',
                            right: '-110px',
                            top: '5px',
                            color: '#aaa',
                            pointerEvents: 'none',
                          }}
                        >
                          Min(s)
                        </Box>
                        <TextField
                          sx={textfieldStyle}
                          autoComplete="off"
                          type="number"
                          min="2"
                          max="1440"
                          value={formData?.authTimeoutLabel}
                          onChange={e => setFormData(prev => ({ ...prev, authTimeoutLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                          error={formData?.authTimeoutLabel < 2 || formData?.authTimeoutLabel > 1440}
                          disabled={!showEditGeneral}
                        />
                        <Typography sx={{ marginTop: 1, color: theme.palette.primary.greySecound }}>(2~1440)</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Authentication Per Day */}
              {showAuthPerDayInfo && (
                <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      ...labelStyle,
                      marginTop: '4px',
                      height: formData?.authPerDayOption === 'CUSTOM' ? '20px' : '38px',
                    }}
                  >
                    <Typography className={mainStyle['collapse-typography']}>Authentication Per Day</Typography>
                  </Box>
                  <Box sx={labelStyle}>
                    <Box sx={{ display: 'flex' }}>
                      <Box sx={{ minWidth: '100px' }}>
                        <RadioButton
                          label="Custom"
                          name="authPerDayOption"
                          value={'CUSTOM'}
                          checked={formData?.authPerDayOption === 'CUSTOM'}
                          onChange={() => setFormData(prev => ({ ...prev, authPerDayOption: 'CUSTOM' }))}
                          disabled={!showEditGeneral}
                        />
                      </Box>
                      <Box sx={{ minWidth: '100px' }}>
                        <RadioButton
                          label="Unlimited"
                          name="authPerDayOption"
                          value={'UNLIMIT'}
                          checked={formData?.authPerDayOption === 'UNLIMIT'}
                          onChange={() => setFormData(prev => ({ ...prev, authPerDayOption: 'UNLIMIT' }))}
                          disabled={!showEditGeneral}
                        />
                      </Box>
                    </Box>

                    {formData?.authPerDayOption === 'CUSTOM' && (
                      <Box sx={{ position: 'relative' }}>
                        <Box
                          sx={{
                            position: 'absolute',
                            right: '-110px',
                            top: '5px',
                            color: '#aaa',
                            pointerEvents: 'none',
                          }}
                        >
                          Time/Day
                        </Box>
                        <TextField
                          sx={textfieldStyle}
                          autoComplete="off"
                          type="number"
                          min="1"
                          max="720"
                          value={formData?.authPerDayLabel}
                          onChange={e => setFormData(prev => ({ ...prev, authPerDayLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                          error={formData?.authPerDayLabel < 1 || formData?.authPerDayLabel > 720}
                          disabled={!showEditGeneral}
                        />

                        <Typography sx={{ marginTop: 1, color: theme.palette.primary.greySecound }}>(1~720)</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Re-Authentication Interval */}
              {showReAuthIntervalInfo && (
                <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      ...labelStyle,
                      marginTop: '10px',
                      height: formData?.reAuthIntervalOption === 'DISABLE' ? '20px' : '50px',
                    }}
                  >
                    <Typography className={mainStyle['collapse-typography']}>Re-Authentication Interval</Typography>
                  </Box>
                  <Box sx={labelStyle}>
                    <Box style={{ display: 'flex' }}>
                      <Box sx={{ minWidth: '100px' }}>
                        <RadioButton
                          label="Disabled"
                          name="reAuthIntervalOption"
                          value={'DISABLE'}
                          checked={formData?.reAuthIntervalOption === 'DISABLE'}
                          onChange={() => setFormData(prev => ({ ...prev, reAuthIntervalOption: 'DISABLE' }))}
                          disabled={!showEditGeneral}
                        />
                      </Box>
                      <Box sx={{ minWidth: '100px' }}>
                        <RadioButton
                          label="Enabled"
                          name="reAuthIntervalOption"
                          value={'ENABLE'}
                          checked={formData?.reAuthIntervalOption === 'ENABLE'}
                          onChange={() => setFormData(prev => ({ ...prev, reAuthIntervalOption: 'ENABLE' }))}
                          disabled={!showEditGeneral}
                        />
                      </Box>
                      <Box sx={{ minWidth: '100px' }}>
                        <RadioButton
                          label="Custom"
                          name="reAuthIntervalOption"
                          value={'CUSTOM'}
                          checked={formData?.reAuthIntervalOption === 'CUSTOM'}
                          onChange={() => setFormData(prev => ({ ...prev, reAuthIntervalOption: 'CUSTOM' }))}
                          disabled={!showEditGeneral}
                        />
                      </Box>
                    </Box>

                    {formData?.reAuthIntervalOption === 'ENABLE' && (
                      <MenuSelect
                        width={textfieldStyle.width}
                        value={formData?.reAuthIntervalList?.find(item => item.isActive)?.value}
                        options={formData?.reAuthIntervalList}
                        onChange={obj => handleChangeItem('reAuthIntervalList', obj.value)}
                        disabled={!showEditGeneral}
                      />
                    )}
                    {formData?.reAuthIntervalOption === 'CUSTOM' && (
                      <Box sx={{ position: 'relative' }}>
                        <Box
                          sx={{
                            position: 'absolute',
                            right: '-110px',
                            top: '5px',
                            color: '#aaa',
                            pointerEvents: 'none',
                          }}
                        >
                          Min(s)
                        </Box>
                        <TextField
                          sx={textfieldStyle}
                          autoComplete="off"
                          type="number"
                          min="1"
                          max="720"
                          value={formData?.reAuthIntervalLabel}
                          onChange={e => setFormData(prev => ({ ...prev, reAuthIntervalLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                          error={formData?.reAuthIntervalLabel < 1 || formData?.reAuthIntervalLabel > 720}
                          disabled={!showEditGeneral}
                        />
                        <Typography sx={{ marginTop: 1, color: theme.palette.primary.greySecound }}>(1~720)</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* User Profile */}
              {showUserProfileInfo && (
                <Box sx={{ ...rowStyle, marginTop: 2 }}>
                  <Box sx={labelStyle}>
                    <Typography className={mainStyle['collapse-typography']}>User Profile</Typography>
                  </Box>
                  <Box sx={labelStyle}>
                    <MenuSelect
                      width={textfieldStyle.width}
                      value={formData?.userProfileList?.find(item => item.isActive)?.value}
                      options={formData?.userProfileList}
                      onChange={obj => handleChangeItem('userProfileList', obj.value)}
                      disabled={!showEditGeneral}
                    />
                  </Box>
                </Box>
              )}

              {/* RADIUS Profile */}
              {showRadiusProfileInfo && (
                <Box sx={{ ...rowStyle, marginTop: 2 }}>
                  <Box sx={labelStyle}>
                    <Typography className={mainStyle['collapse-typography']}>RADIUS Profile</Typography>
                  </Box>
                  <Box sx={labelStyle}>
                    <MenuSelect
                      width={textfieldStyle.width}
                      value={formData?.radiusProfileList?.find(item => item.isActive)?.value}
                      options={formData?.radiusProfileList}
                      onChange={obj => handleChangeItem('radiusProfileList', obj.value)}
                      disabled={!showEditGeneral}
                    />
                  </Box>
                </Box>
              )}

              {/* LDAP Profile */}
              {showLdapProfileInfo && (
                <Box sx={{ ...rowStyle, marginTop: 2 }}>
                  <Box sx={labelStyle}>
                    <Typography className={mainStyle['collapse-typography']}>LDAP Profile</Typography>
                  </Box>
                  <Box sx={labelStyle}>
                    <MenuSelect
                      width={textfieldStyle.width}
                      value={formData?.ldapProfileList?.find(item => item.isActive)?.value}
                      options={formData?.ldapProfileList}
                      onChange={obj => handleChangeItem('ldapProfileList', obj.value)}
                      disabled={!showEditGeneral}
                    />
                  </Box>
                </Box>
              )}

              {/* External Captive Portal */}
              {showExternalCaptivePortalInfo && (
                <Box sx={{ ...rowStyle, marginTop: 2 }}>
                  <Box sx={labelStyle}>
                    <Typography className={mainStyle['collapse-typography']}>External Captive Portal</Typography>
                  </Box>
                  <Box sx={labelStyle}>
                    <UrlInput
                      maxLength={formData?.externalCaptivePortalProtocol === 'https' ? 2075 : 2076} // Custom landing page URL max 2083 characters.
                      protocol={formData?.externalCaptivePortalProtocol}
                      onChangeProtocol={protocol => setFormData(prev => ({ ...prev, externalCaptivePortalProtocol: protocol }))}
                      inputValue={formData?.externalCaptivePortalLabel || ''}
                      onChangeInputValue={newUrlInputValue => setFormData(prev => ({ ...prev, externalCaptivePortalLabel: newUrlInputValue }))}
                      error={
                        (!isNil(formData?.externalCaptivePortalLabel) && size(formData?.externalCaptivePortalLabel) === 0) ||
                        (size(formData?.externalCaptivePortalLabel) > 0 && !validUrl(formData?.externalCaptivePortalLabel))
                      }
                      disabled={!showEditGeneral}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </CollapseCard>

          {/* Remove Profile Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              width: '100%',
            }}
          >
            <Button
              onMouseEnter={() => setIsDeleteHovered(true)}
              onMouseLeave={() => setIsDeleteHovered(false)}
              startIcon={isDeleteHovered ? <BinHoverIcon /> : <BinIcon />}
              sx={{
                color: theme.palette.primary.main,
                fontSize: theme.typography.customFontSize[3],
                fontFamily: theme.typography.fontFamily,
                backgroundColor: theme.palette.primary.transparent,
                '&:hover': {
                  backgroundColor: theme.palette.primary.transparent,
                  color: theme.palette.primary.mainHover,
                },
                '&:active': {
                  backgroundColor: theme.palette.primary.transparent,
                  color: theme.palette.primary.main,
                },
                '&:disabled': {
                  color: theme.palette.action.disabled,
                },
              }}
              onClick={() => setOpenRemoveProfileModal(true)}
              disabled={!showEditGeneral}
            >
              Remove Profile
            </Button>
          </Box>
        </Box>
      )}

      {/* Tab - Advanced */}
      {tabValue === 'advanced' && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            px: 4,
            boxSizing: 'border-box',
            gap: '20px',
          }}
        >
          {/* Landing Page */}
          {showLandingPageInfo && (
            <CollapseCard title="Landing Page">
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography className={mainStyle['collapse-typography']}>Clients will be directed to the page after they pass Portal authentication.</Typography>

                <Box sx={{ display: 'grid', marginLeft: '10px' }}>
                  <RadioButton
                    label="Not Specified"
                    name="landingPageOption"
                    value={'NO_SPECIFIC'}
                    checked={formData?.landingPageOption === 'NO_SPECIFIC'}
                    onChange={() => setFormData(prev => ({ ...prev, landingPageOption: 'NO_SPECIFIC', landingPageUrlProtocol: 'http', landingPageUrlLabel: '' }))}
                    disabled={!showEditGeneral}
                  />
                  <RadioButton
                    label="The Promotional URL"
                    name="landingPageOption"
                    value={'CUSTOM'}
                    checked={formData?.landingPageOption === 'CUSTOM'}
                    onChange={() => setFormData(prev => ({ ...prev, landingPageOption: 'CUSTOM' }))}
                    disabled={!showEditGeneral}
                  />
                  {formData?.landingPageOption === 'CUSTOM' && (
                    <Box sx={{ width: '200px', marginBottom: '10px' }}>
                      <UrlInput
                        maxLength={formData?.landingPageUrlProtocol === 'https' ? 2075 : 2076} // Custom landing page URL max 2083 characters.
                        placeholder="www.dlink.com"
                        protocol={formData?.landingPageUrlProtocol}
                        onChangeProtocol={protocol => setFormData(prev => ({ ...prev, landingPageUrlProtocol: protocol }))}
                        inputValue={formData?.landingPageUrlLabel || ''}
                        onChangeInputValue={newUrlInputValue => setFormData(prev => ({ ...prev, landingPageUrlLabel: newUrlInputValue }))}
                        disabled={!showEditGeneral}
                        error={
                          (!isNil(formData?.landingPageUrlLabel) && size(formData?.landingPageUrlLabel) === 0) ||
                          (size(formData?.landingPageUrlLabel) > 0 && !validUrl(formData?.landingPageUrlLabel))
                        }
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </CollapseCard>
          )}

          {/* Whitelist */}
          {showWhitelistInfo && (
            <CollapseCard title="Whitelist">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <>
                  <Typography className={mainStyle['collapse-typography']} sx={{ whiteSpace: 'normal' }}>
                    To set up the Whitelist, add one or more existing MAC Address Profiles. A maximum of 64 profiles can be configured.
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: 1 }}>
                    <SelectMenu menuList={formData?.whitelistList} onChangeItem={newValue => handleChangeItem('whitelistList', newValue)} disabled={!showEditGeneral} />

                    {macLimitExceeded && (
                      <Box sx={{ marginTop: '8px', paddingLeft: '14px' }}>
                        <Typography sx={{ fontSize: '12px', color: theme.palette.primary.redPrimary }}>
                          The selected MAC Address Profile has exceeded the maximum limit of 64 MAC addresses.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              </Box>
            </CollapseCard>
          )}

          {/* Pre-Auth. Access (Walled Garden) */}
          {showPreAuthAccessInfo && (
            <CollapseCard title="Pre-Auth. Access (Walled Garden)">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <>
                  <Box>
                    <div style={{ display: 'flex' }}>
                      <div style={{ width: 100 }}>
                        <RadioButton
                          label="Enabled"
                          name="preAuthAccessOption"
                          value={'ENABLE'}
                          checked={formData?.preAuthAccessOption === 'ENABLE'}
                          onChange={() => setFormData(prev => ({ ...prev, preAuthAccessOption: 'ENABLE' }))}
                          disabled={!showEditGeneral}
                        />
                      </div>
                      <div style={{ width: 100 }}>
                        <RadioButton
                          label="Disabled"
                          name="preAuthAccessOption"
                          value={'DISABLE'}
                          checked={formData?.preAuthAccessOption === 'DISABLE'}
                          onChange={() => setFormData(prev => ({ ...prev, preAuthAccessOption: 'DISABLE' }))}
                          disabled={!showEditGeneral}
                        />
                      </div>
                    </div>
                  </Box>
                  {formData?.preAuthAccessOption === 'ENABLE' && (
                    <Box>
                      {/* Walled Garden List */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <Box>
                          <Typography
                            sx={{
                              color: theme.palette.primary.greyPrimary,
                              fontSize: theme.typography.customFontSize[2],
                              fontWeight: theme.typography.customFontWeight[1],
                            }}
                          >
                            Walled Garden List
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex' }}>
                          <Typography sx={{ fontSize: theme.typography.customFontSize[2], fontWeight: 'bold', paddingRight: '5px' }}>
                            {formData?.walledGardenList?.length}
                          </Typography>
                          <Typography sx={{ fontSize: theme.typography.customFontSize[2] }}>/ 20 Entries Used</Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: theme.typography.customFontSize[2] }}>Please enter a valid IP address, IP address/subnet, or domain name.</Typography>
                      <Box
                        className={mainStyle['custom-scrollbar']}
                        sx={{
                          marginTop: '10px',
                          border: `1px solid ${theme.palette.primary.greyThread}`,
                          borderRadius: '10px',
                          maxHeight: '242px',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          boxSizing: 'content-box',
                        }}
                      >
                        {formData?.walledGardenList?.map((item, index) => (
                          <Box
                            key={`${index}-${item?.value || item?.label || 'empty'}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: index % 2 === 0 ? theme.palette.primary.transparent : theme.palette.primary.whiteSecound,
                              borderBottomLeftRadius: formData?.walledGardenList?.length - 1 === index ? '10px' : '0px',
                              borderBottomRightRadius: formData?.walledGardenList?.length - 1 === index ? '10px' : '0px',
                            }}
                          >
                            <TextField
                              sx={{
                                ...textfieldStyle,
                                width: '100%',
                                minWidth: '300px',
                                padding: '15px 0px 15px 15px',
                              }}
                              autoComplete="off"
                              inputProps={{
                                minLength: 1,
                                maxLength: 64,
                              }}
                              value={walledGardenInputs[index] || ''}
                              onChange={e =>
                                setWalledGardenInputs(prev => {
                                  const next = [...prev];
                                  next[index] = e.target.value;
                                  return next;
                                })
                              }
                              onBlur={() => handleChangeWalledGardenItem(index, walledGardenInputs[index] || '')}
                              error={(Boolean(walledGardenInputs[index]) && !isValidWalledGardenValue(walledGardenInputs[index])) || isWalledGardenValueDuplicate(index)}
                              disabled={!showEditGeneral}
                            />
                            <Box sx={{ width: '50px', paddingLeft: '10px' }}>
                              {index !== 0 && (
                                <IconButton
                                  sx={{ color: theme.palette.primary.main }}
                                  aria-label={`remove-walled-garden-${index}`}
                                  onClick={() => handleRemoveWalledGardenItem(index)}
                                  disabled={!showEditGeneral}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      {/* Add button */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                        <OutlineRectangleButton
                          startIcon={!(formData?.walledGardenList?.length || 0) >= MAX_WALLED_GARDEN_ENTRIES ? <AddIcon /> : <AddIconDisabled />}
                          width="80px"
                          height="30px"
                          onClick={handleAddWalledGardenItem}
                          disabled={!showEditGeneral || (formData?.walledGardenList?.length || 0) >= MAX_WALLED_GARDEN_ENTRIES}
                        >
                          Add
                        </OutlineRectangleButton>
                      </Box>
                    </Box>
                  )}
                </>
              </Box>
            </CollapseCard>
          )}
        </Box>
      )}

      {hasChanged && (
        <Box
          sx={{
            position: 'fixed',
            bottom: '15px',
            right: '30px',
            zIndex: 1000,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'primary.lightBlue',
              width: '530px',
              height: '64px',
              gap: 2,
            }}
          >
            <OutlineButton width="100px" height="32px" onClick={handleChangeResetBtn}>
              Cancel
            </OutlineButton>
            <SolidButton width="100px" height="32px" onClick={checkSaveProRule} disabled={!showEditGeneral || checkConfigInvalid()}>
              Apply
            </SolidButton>
          </Box>
        </Box>
      )}

      <LinkModal
        transparentBackdrop
        open={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        onLinkClick={() => {
          setIsLinkModalOpen(false);
          const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
          navigate(isMspScope ? '/msp/license' : '/license');
        }}
      />

      <GlobalAlert open={alertInfo.open} severity={alertInfo.severity} message={alertInfo.message} onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} />
    </Box>
  );
};
export default EditCaptivePortalContent;
